import { Ref, useState } from "react";
import cn from "classnames";
import { Tooltip } from '@components/new/Tooltip';
import { Title2, Title3 } from "@components/new/Typo";
import { LinkButton, LinkButtonSize, LinkButtonProps } from "@components/new/LinkButton";
import styles from "./Textarea.module.scss";

interface TextInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  hasError?: boolean;
  onClick?: (e: any) => void;
  errorMessage?: string;
  name?: string;
  ref?: Ref<HTMLTextAreaElement>;
  title: string;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  maxLength?: number;
  disabledMessage?: string;
  className?: string;
  description?: string;
  button?: LinkButtonProps;
}

const TooltipContainer = ({ disabledMessage, children }: {
  disabledMessage?: string,
  children?: string | JSX.Element | JSX.Element[],
}) => {
  if (disabledMessage) {
    return (
      <Tooltip
        action={<>{children}</>}
        position="top"
        text={disabledMessage}
      />
    )
  }
  return (<>{children}</>)
}

const Textarea = (props: TextInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const isFilled = props.value && props.value.length > 0;

  const handleFocus = () => {
    setIsFocused(true);
    if (props.onFocus) {
      props.onFocus();
    }
  };
  const handleBlur = () => {
    setIsFocused(false);
    if (props.onBlur) {
      props.onBlur();
    }
  };

  const showError = props.hasError && props.errorMessage;
  const showFooter = showError || props.description || props.button;

  return (
    <div
      className={cn(
        props.className,
        styles.container,
        (isFocused || isFilled) && styles.active,
        props.hasError && styles.error,
        isFocused && styles.focus,
        props.disabled && styles.disabled,
      )}
    >
      {props.title ? (
        <Title2 className={styles.title}>{props.title}</Title2>
      ) : null}
      <div className={styles.inputWrapper}>
        <TooltipContainer disabledMessage={props.disabled && props.disabledMessage || ""} >
          <div className={styles.wrapper} onClick={props.onClick}>
            <textarea
              ref={props.ref}
              name={props.name}
              value={props.value}
              placeholder={props.placeholder}
              className={styles.input}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={(e) => {
                if (props.onChange) {
                  props.onChange((e.target.value || "").replace(/(?!\r)\n/g, '\r\n').slice(0, props.maxLength));
                }
              }}
              disabled={props.disabled}
              rows={props.rows}
              maxLength={props.maxLength}
            />
          </div>
        </TooltipContainer>
      </div>
      {showFooter ? (
        <div className={styles.footer}>
          {(showError) ? (
            <Title3>{props.errorMessage}</Title3>
          ) : (
            props.description ? (
              <Title3>{props.description}</Title3>
            ) : null
          )}
          {props.button ? (
            <LinkButton
              size={LinkButtonSize.L}
              {...props.button}
              className={styles.footerButton}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default Textarea;
